<template>
  <div
    class="story-viewer-overlay"
    :style="overlayStyle"
    @touchstart.passive="handleTouchStart"
    @touchmove.passive="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <div class="story-viewer-container" :style="containerStyle">
      <!-- ═══════════════════════════════════════════════════════════ -->
      <!-- LAYER 1: Story Content (z-index: 1) -->
      <!-- ═══════════════════════════════════════════════════════════ -->
      <div
        class="story-content"
        @mousedown="handleLongPressStart"
        @mouseup="handleLongPressEnd"
        @touchstart.passive="handleLongPressStart"
        @touchend="handleLongPressEnd"
      >
        <!-- Loading spinner -->
        <div v-if="!isContentLoaded && currentStatus" class="loading-spinner">
          <div class="spinner"></div>
        </div>

        <!-- Text Status -->
        <div v-if="currentStatus?.type === 'text'" class="text-status-content">
          <p
            class="text-overlay"
            v-html="linkifyText(currentStatus.textOverlay || '')"
            @click="handleDescriptionClick"
          ></p>
        </div>

        <!-- Image Status -->
        <img
          v-else-if="
            currentStatus?.type === 'image' && currentStatus.mediaDeliveryUrl
          "
          :src="currentStatus.mediaDeliveryUrl"
          class="media-content"
          @load="handleImageLoad"
          @error="handleMediaError"
          alt="Status"
        />

        <!-- Video Status -->
        <video
          v-else-if="
            currentStatus?.type === 'video' && currentStatus.mediaDeliveryUrl
          "
          ref="videoElement"
          :src="currentStatus.mediaDeliveryUrl"
          class="media-content"
          playsinline
          @loadedmetadata="handleVideoLoad"
          @error="handleMediaError"
          @ended="goToNextStatus"
        ></video>

        <!-- Error state -->
        <div v-if="mediaError" class="media-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p>{{ $t("stories.failed_to_load") }}</p>
        </div>

        <!-- Pause Icon -->
        <Transition name="fade">
          <div v-if="showPauseIcon && !isInputFocused" class="pause-indicator">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="44"
              height="44"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <rect x="6" y="4" width="4" height="16" rx="1"></rect>
              <rect x="14" y="4" width="4" height="16" rx="1"></rect>
            </svg>
          </div>
        </Transition>

        <!-- Heart Animation -->
        <Transition name="heart-pop">
          <div v-if="showingHeartAnimation" class="heart-animation">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="80"
              height="80"
              viewBox="0 0 24 24"
              fill="#ff3b5c"
              stroke="none"
            >
              <path
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              ></path>
            </svg>
          </div>
        </Transition>
      </div>

      <!-- ═══════════════════════════════════════════════════════════ -->
      <!-- LAYER 2: Navigation Buttons (z-index: 10) -->
      <!-- RTL-aware: Left/Right buttons swap logic based on direction -->
      <!-- ═══════════════════════════════════════════════════════════ -->
      <button
        class="nav-button nav-button-left"
        @click.stop="handleLeftButton"
        :aria-label="isRTL ? 'Next' : 'Previous'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <button
        class="nav-button nav-button-right"
        @click.stop="handleRightButton"
        :aria-label="isRTL ? 'Previous' : 'Next'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>

      <!-- ═══════════════════════════════════════════════════════════ -->
      <!-- LAYER 3: UI Overlays (z-index: 20) - ALWAYS VISIBLE -->
      <!-- ═══════════════════════════════════════════════════════════ -->
      <div class="ui-overlays">
        <!-- Top gradient -->
        <div class="gradient-top"></div>

        <!-- Top UI: Progress bars + User info -->
        <div class="top-ui">
          <!-- Progress bars -->
          <div class="progress-bars">
            <div
              v-for="(status, index) in currentGroup?.statuses || []"
              :key="status.id"
              class="progress-bar-wrapper"
            >
              <div class="progress-bar-bg">
                <div
                  class="progress-bar-fill"
                  :style="getProgressStyle(index)"
                ></div>
              </div>
            </div>
          </div>

          <!-- User info row -->
          <div class="user-info-row">
            <div class="user-avatar">
              <img
                v-if="currentGroup?.user?.displayImage"
                :src="currentGroup.user.displayImage"
                :alt="currentGroup?.user?.displayName"
                @error="handleAvatarError"
              />
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>

            <div class="user-details">
              <span class="user-name">{{
                currentGroup?.user?.displayName || $t("stories.unknown_user")
              }}</span>
              <span class="timestamp">{{
                formatTimeAgo(currentStatus?.publishedAt)
              }}</span>
            </div>

            <!-- Popup menu (only for own status) -->
            <button
              v-if="isOwnStatus"
              class="menu-button"
              @click.stop="showPopupMenu = !showPopupMenu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="1"></circle>
                <circle cx="12" cy="5" r="1"></circle>
                <circle cx="12" cy="19" r="1"></circle>
              </svg>
            </button>

            <!-- Close button -->
            <button class="close-button" @click.stop="handleClose">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- Bottom UI: Description + Input -->
        <div class="bottom-ui">
          <!-- Description -->
          <div v-if="currentStatus?.description" class="description-section">
            <p
              v-if="currentStatus.textOverlay && currentStatus.type !== 'text'"
              class="text-overlay-label"
              v-html="linkifyText(currentStatus.textOverlay)"
              @click="handleDescriptionClick"
            ></p>
            <p
              class="description-text"
              v-html="linkifyText(currentStatus.description)"
              @click="handleDescriptionClick"
            ></p>
          </div>

          <!-- ═══════════════════════════════════════════════════════════ -->
          <!-- INPUT BAR - ALWAYS RENDERED, visibility controlled by CSS -->
          <!-- ═══════════════════════════════════════════════════════════ -->
          <div class="input-bar" :class="{ hidden: isOwnStory }">
            <input
              ref="replyInput"
              v-model="replyMessage"
              type="text"
              :placeholder="$t('stories.send_message')"
              maxlength="2000"
              @focus="handleInputFocus"
              @blur="handleInputBlur"
              @keyup.enter="sendReply"
            />

            <!-- Reaction button -->
            <button
              class="action-button reaction-button"
              :class="{ reacted: hasReacted, loading: isTogglingReaction }"
              @click.stop="toggleReaction"
            >
              <div v-if="isTogglingReaction" class="button-spinner"></div>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                :fill="hasReacted ? 'currentColor' : 'none'"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                ></path>
              </svg>
            </button>

            <!-- Send button -->
            <button
              class="action-button send-button"
              :disabled="isSendingReply || !replyMessage.trim()"
              @click.stop="sendReply"
            >
              <div v-if="isSendingReply" class="button-spinner"></div>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Popup Menu -->
      <Transition name="fade">
        <div
          v-if="showPopupMenu"
          class="popup-menu-overlay"
          @click="showPopupMenu = false"
        >
          <div class="popup-menu" @click.stop>
            <button
              v-if="currentStatus?.reactionCount > 0"
              class="popup-item"
              @click="showReactionsSheet"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                ></path>
              </svg>
              <span>{{ $t("stories.reactions") }}</span>
              <span class="reaction-count">{{
                currentStatus?.reactionCount
              }}</span>
            </button>
            <button class="popup-item delete" @click="confirmDelete">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path>
              </svg>
              <span>{{ $t("stories.delete") }}</span>
            </button>
          </div>
        </div>
      </Transition>

      <!-- Delete Confirmation Dialog -->
      <Transition name="fade">
        <div
          v-if="showDeleteConfirm"
          class="confirm-dialog-overlay"
          @click="showDeleteConfirm = false"
        >
          <div class="confirm-dialog" @click.stop>
            <h3>{{ $t("stories.delete_status") }}</h3>
            <p>{{ $t("stories.delete_status_confirmation") }}</p>
            <div class="dialog-actions">
              <button class="cancel-btn" @click="showDeleteConfirm = false">
                {{ $t("stories.cancel") }}
              </button>
              <button class="delete-btn" @click="deleteCurrentStatus">
                {{ $t("stories.delete") }}
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Reactions Bottom Sheet -->
      <Transition name="slide-up">
        <div
          v-if="showReactions"
          class="reactions-sheet-overlay"
          @click="showReactions = false"
        >
          <div class="reactions-sheet" @click.stop>
            <div class="sheet-handle"></div>
            <div class="sheet-header">
              <h3>{{ $t("stories.reactions") }}</h3>
              <span class="total-count">{{ reactions.length }}</span>
            </div>
            <div class="reactions-list">
              <div v-if="loadingReactions" class="loading-reactions">
                <div class="spinner"></div>
              </div>
              <div v-else-if="reactions.length === 0" class="no-reactions">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1"
                >
                  <path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  ></path>
                </svg>
                <p>{{ $t("stories.no_reactions_yet") }}</p>
              </div>
              <div
                v-else
                v-for="reaction in reactions"
                :key="reaction.id"
                class="reaction-item"
              >
                <div class="reaction-user-avatar">
                  <img
                    v-if="reaction.user?.image"
                    :src="reaction.user.image"
                    :alt="reaction.user?.name"
                  />
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <div class="reaction-user-info">
                  <span class="reaction-user-name">{{
                    reaction.user?.name || $t("stories.unknown_user")
                  }}</span>
                  <span class="reaction-time">{{
                    formatTimeAgo(reaction.createdAt)
                  }}</span>
                </div>
                <span class="reaction-emoji">{{
                  getReactionEmoji(reaction.type)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Delete Loading Overlay -->
      <Transition name="fade">
        <div v-if="isDeleting" class="delete-loading-overlay">
          <div class="spinner large"></div>
        </div>
      </Transition>

      <!-- Toast Notification -->
      <Transition name="toast-slide">
        <div v-if="showToast" class="toast-notification">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M22 2L11 13"></path>
            <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
          </svg>
          <span>{{ toastMessage }}</span>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
/**
 * StoryViewer Component - REBUILT
 *
 * ARCHITECTURE:
 * - Layer 1 (z-index: 1): Story content (images/videos/text)
 * - Layer 2 (z-index: 10): Navigation buttons
 * - Layer 3 (z-index: 20): UI overlays (progress, user info, input)
 *
 * INTERACTION MODEL:
 * - Navigation: ONLY via nav buttons (no tap zones, no mouse events)
 * - Pause: Long press on content OR input focus
 * - Resume: Release long press OR input blur
 * - Input: Always visible, never triggers navigation
 */

import { ref, computed, watch, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useStories } from "@/composables/useStories";
import { useStoriesStore } from "@/stores/stories";
import { StoriesApi } from "@/services/storiesApi";

const { t } = useI18n();

// ═══════════════════════════════════════════════════════════
// PROPS & EMITS
// ═══════════════════════════════════════════════════════════

const props = defineProps({
  initialGroupIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["close"]);

function handleClose() {
  emit("close");
}

// ═══════════════════════════════════════════════════════════
// COMPOSABLES
// ═══════════════════════════════════════════════════════════

const store = useStoriesStore();

const {
  currentGroupIndex,
  currentStatusIndex,
  isContentLoaded,
  showPauseIcon,
  progress,
  dragOffset,
  dragScale,
  isDragging,
  currentGroup,
  currentStatus,
  goToNextStatus,
  goToPreviousStatus,
  pausePlayback,
  resumePlayback,
  onContentLoaded,
  onDragStart,
  onDragUpdate,
  onDragEnd,
  setVideoRef,
} = useStories(props.initialGroupIndex, handleClose);

// ═══════════════════════════════════════════════════════════
// LOCAL STATE
// ═══════════════════════════════════════════════════════════

const videoElement = ref(null);
const replyInput = ref(null);
const replyMessage = ref("");
const mediaError = ref(false);
const showPopupMenu = ref(false);
const showDeleteConfirm = ref(false);
const showReactions = ref(false);
const reactions = ref([]);
const loadingReactions = ref(false);
const isTogglingReaction = ref(false);
const isSendingReply = ref(false);
const isDeleting = ref(false);
const showingHeartAnimation = ref(false);
const toastMessage = ref("");
const showToast = ref(false);

// Local reaction state for optimistic UI (null = use store value)
const localReactionState = ref(null);

// Input focus tracking (local, not from composable)
const isInputFocused = ref(false);

// Long press state
let longPressTimer = null;
let isLongPressing = false;

// Drag state
let touchStartY = 0;
let touchStartTime = 0;

// ═══════════════════════════════════════════════════════════
// COMPUTED
// ═══════════════════════════════════════════════════════════

// Simple check: is this the current user's story?
const isOwnStatus = computed(() => {
  const group = currentGroup.value;
  if (!group) return false;

  // Get current user ID from JWT token (same method as stories store)
  let currentUserId = null;
  try {
    const token = localStorage.getItem("access_token");
    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      currentUserId = payload.sub || payload.user_id || payload.id;
    }
  } catch (_) {}

  // Fallback to localStorage user_id
  if (!currentUserId) {
    currentUserId = localStorage.getItem("user_id");
  }

  if (!currentUserId) return false;

  // Compare user IDs
  const storyUserId =
    group.userId || group.user?.id || currentStatus.value?.userId;
  return String(currentUserId) === String(storyUserId);
});

// Alias for template usage (input bar visibility)
const isOwnStory = isOwnStatus;

// RTL Detection - checks document direction
const isRTL = computed(() => {
  return document.dir === "rtl" || document.documentElement.dir === "rtl";
});

// Effective reaction state (optimistic UI: local state overrides store)
const hasReacted = computed(() => {
  if (localReactionState.value !== null) {
    return localReactionState.value;
  }
  return currentStatus.value?.userHasReacted ?? false;
});

const overlayStyle = computed(() => ({
  backgroundColor: `rgba(0, 0, 0, ${Math.max(
    0.3,
    1 - dragOffset.value / 400
  )})`,
}));

const containerStyle = computed(() => ({
  transform: `translateY(${dragOffset.value}px) scale(${dragScale.value})`,
  opacity: 1 - dragOffset.value / 800,
}));

// ═══════════════════════════════════════════════════════════
// NAVIGATION HANDLERS (RTL-aware)
// ═══════════════════════════════════════════════════════════
// In LTR: Left = Previous, Right = Next
// In RTL: Left = Next, Right = Previous (natural flow)

function handleLeftButton() {
  if (isRTL.value) {
    goToNextStatus();
  } else {
    goToPreviousStatus();
  }
}

function handleRightButton() {
  if (isRTL.value) {
    goToPreviousStatus();
  } else {
    goToNextStatus();
  }
}

// ═══════════════════════════════════════════════════════════
// LONG PRESS HANDLERS (Pause/Resume)
// ═══════════════════════════════════════════════════════════

function handleLongPressStart(event) {
  // Ignore if clicking on UI elements
  if (
    event.target.closest(".ui-overlays") ||
    event.target.closest(".nav-button") ||
    event.target.closest("input") ||
    event.target.closest("button")
  ) {
    return;
  }

  isLongPressing = false;
  longPressTimer = setTimeout(() => {
    isLongPressing = true;
    pausePlayback();
  }, 200);
}

function handleLongPressEnd() {
  if (longPressTimer) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }

  if (isLongPressing && !isInputFocused.value) {
    resumePlayback();
  }
  isLongPressing = false;
}

// ═══════════════════════════════════════════════════════════
// SWIPE TO DISMISS
// ═══════════════════════════════════════════════════════════

function handleTouchStart(event) {
  // Don't start drag from input area
  if (event.target.closest(".input-bar")) return;

  touchStartY = event.touches[0].clientY;
  touchStartTime = Date.now();
}

function handleTouchMove(event) {
  if (event.target.closest(".input-bar")) return;

  const deltaY = event.touches[0].clientY - touchStartY;

  if (deltaY > 15 && !isDragging.value) {
    onDragStart();
  }

  if (isDragging.value) {
    onDragUpdate(deltaY - dragOffset.value);
    touchStartY = event.touches[0].clientY - dragOffset.value;
  }
}

function handleTouchEnd() {
  if (!isDragging.value) return;

  const velocity = dragOffset.value / ((Date.now() - touchStartTime) / 1000);
  if (onDragEnd(velocity)) {
    handleClose();
  }
}

// ═══════════════════════════════════════════════════════════
// INPUT HANDLERS
// ═══════════════════════════════════════════════════════════

function handleInputFocus() {
  isInputFocused.value = true;
  pausePlayback();
}

function handleInputBlur() {
  isInputFocused.value = false;
  // Small delay to allow click events to process
  setTimeout(() => {
    if (!isInputFocused.value && !isLongPressing) {
      resumePlayback();
    }
  }, 150);
}

// ═══════════════════════════════════════════════════════════
// MEDIA HANDLERS
// ═══════════════════════════════════════════════════════════

function handleImageLoad() {
  mediaError.value = false;
  onContentLoaded(5000);
}

function handleVideoLoad() {
  mediaError.value = false;
  if (videoElement.value) {
    setVideoRef(videoElement.value);
    onContentLoaded(videoElement.value.duration * 1000);
    videoElement.value.play();
  }
}

function handleMediaError() {
  mediaError.value = true;
  onContentLoaded(5000);
}

function handleAvatarError(event) {
  event.target.style.display = "none";
}

// ═══════════════════════════════════════════════════════════
// PROGRESS BAR
// ═══════════════════════════════════════════════════════════

function getProgressStyle(index) {
  if (index < currentStatusIndex.value) {
    return { width: "100%" };
  } else if (index === currentStatusIndex.value) {
    return { width: `${progress.value * 100}%` };
  }
  return { width: "0%" };
}

// ═══════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════

function formatTimeAgo(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return t("stories.just_now");
    if (diffMin < 60) return t("stories.minutes_ago", { count: diffMin });
    if (diffHour < 24) return t("stories.hours_ago", { count: diffHour });
    if (diffDay < 7) return t("stories.days_ago", { count: diffDay });
    return date.toLocaleDateString();
  } catch {
    return "";
  }
}

function getReactionEmoji(type) {
  const map = {
    love: "❤️",
    heart: "❤️",
    fire: "🔥",
    clap: "👏",
    like: "👍",
    laugh: "😂",
    wow: "😮",
    sad: "😢",
    angry: "😡",
  };
  return map[type?.toLowerCase()] || "❤️";
}

/**
 * Converts URLs in text to clickable hyperlinks.
 * Matches http://, https://, and www. URLs.
 */
function linkifyText(text) {
  if (!text) return "";

  // Escape HTML to prevent XSS
  const escapeHtml = (str) => {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  };

  const urlRegex =
    /https?:\/\/[^\s<>\[\]{}|\\^`"]+|www\.[^\s<>\[\]{}|\\^`"]+/gi;

  let result = "";
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    // Add escaped text before the URL
    result += escapeHtml(text.substring(lastIndex, match.index));

    const url = match[0];
    const href = url.startsWith("www.") ? `https://${url}` : url;

    // Add the clickable link
    result += `<a href="${escapeHtml(
      href
    )}" target="_blank" rel="noopener noreferrer" class="story-link">${escapeHtml(
      url
    )}</a>`;

    lastIndex = urlRegex.lastIndex;
  }

  // Add remaining escaped text
  result += escapeHtml(text.substring(lastIndex));

  return result;
}

/**
 * Handles clicks on the description text.
 * Stops propagation when clicking on links to prevent story navigation.
 */
function handleDescriptionClick(event) {
  if (event.target.tagName === "A") {
    event.stopPropagation();
  }
}

// ═══════════════════════════════════════════════════════════
// ACTIONS
// ═══════════════════════════════════════════════════════════

async function toggleReaction() {
  if (isTogglingReaction.value || !currentStatus.value) return;

  const statusId = currentStatus.value.id;
  const wasReacted = currentStatus.value.userHasReacted ?? false;

  console.log(
    "[StoryViewer] toggleReaction - statusId:",
    statusId,
    "wasReacted:",
    wasReacted
  );

  // Optimistic UI update - immediately show the change
  isTogglingReaction.value = true;
  localReactionState.value = !wasReacted;

  // Show heart animation immediately for positive reaction
  if (!wasReacted) {
    showingHeartAnimation.value = true;
    setTimeout(() => {
      showingHeartAnimation.value = false;
    }, 1000);
  }

  try {
    // Call API
    const result = await store.toggleReaction(statusId);
    console.log("[StoryViewer] toggleReaction - API result:", result);

    if (result.success) {
      // Success - clear local state, use store value
      localReactionState.value = null;
    } else {
      // API returned error - revert and show error message
      localReactionState.value = wasReacted;
      showToastNotification(
        result.message || t("stories.reaction_failed") || "Failed to react"
      );
    }
  } catch (e) {
    // Error - revert
    localReactionState.value = wasReacted;
    console.error("[StoryViewer] Reaction failed:", e);
    showToastNotification(
      e.message || t("stories.reaction_failed") || "Failed to react"
    );
  } finally {
    isTogglingReaction.value = false;
  }
}

function showToastNotification(message) {
  toastMessage.value = message;
  showToast.value = true;
  setTimeout(() => {
    showToast.value = false;
  }, 3000);
}

async function sendReply() {
  const message = replyMessage.value.trim();
  if (!message || isSendingReply.value || !currentStatus.value) return;

  isSendingReply.value = true;
  try {
    await StoriesApi.replyToStatus(currentStatus.value.id, message);
    replyMessage.value = "";
    replyInput.value?.blur();
    showToastNotification(
      t("stories.reply_sent_successfully") || "Reply sent!"
    );
  } catch (e) {
    console.error("Reply failed:", e);
    // Show the actual error message from API
    showToastNotification(
      e.message || t("stories.reply_failed") || "Failed to send"
    );
  } finally {
    isSendingReply.value = false;
  }
}

function confirmDelete() {
  showPopupMenu.value = false;
  showDeleteConfirm.value = true;
  pausePlayback();
}

async function deleteCurrentStatus() {
  if (!currentStatus.value) return;

  showDeleteConfirm.value = false;
  isDeleting.value = true;

  const success = await store.deleteStatus(currentStatus.value.id);
  isDeleting.value = false;

  if (success) {
    if (
      store.statusGroups.length === 0 ||
      currentGroupIndex.value >= store.statusGroups.length
    ) {
      handleClose();
    } else if (!currentGroup.value?.statuses?.length) {
      handleClose();
    }
  } else {
    resumePlayback();
  }
}

async function showReactionsSheet() {
  showPopupMenu.value = false;
  showReactions.value = true;
  loadingReactions.value = true;
  pausePlayback();

  try {
    reactions.value = await StoriesApi.getReactions(currentStatus.value.id);
  } catch {
    reactions.value = [];
  } finally {
    loadingReactions.value = false;
  }
}

// ═══════════════════════════════════════════════════════════
// WATCHERS
// ═══════════════════════════════════════════════════════════

watch(videoElement, (el) => {
  if (el) setVideoRef(el);
});

watch(currentStatus, () => {
  mediaError.value = false;
  // Reset local reaction state when status changes
  localReactionState.value = null;
});

// ═══════════════════════════════════════════════════════════
// LIFECYCLE
// ═══════════════════════════════════════════════════════════

onUnmounted(() => {
  if (longPressTimer) clearTimeout(longPressTimer);
});
</script>

<style scoped>
.story-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 1);
  transition: background-color 0.2s ease;
}

.story-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 500px;
  background: #000;
  overflow: hidden;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

@media (min-width: 768px) {
  .story-viewer-container {
    height: 90vh;
    border-radius: 16px;
  }
}

/* Navigation buttons */
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  z-index: 15;
  transition: background 0.2s ease, opacity 0.2s ease;
  opacity: 0.7;
}

.nav-button:hover {
  background: rgba(0, 0, 0, 0.6);
  opacity: 1;
}

.nav-button:active {
  transform: translateY(-50%) scale(0.95);
}

.nav-button-left {
  left: 8px;
}

.nav-button-right {
  right: 8px;
}

.story-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  z-index: 1;
}

/* Text status */
.text-status-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: linear-gradient(135deg, #00c444 0%, #00a43c 50%, #008836 100%);
}

.text-overlay {
  font-size: 24px;
  font-weight: bold;
  color: white;
  text-align: center;
  word-wrap: break-word;
}

.text-overlay :deep(.story-link),
.text-overlay :deep(a) {
  color: #bfdbfe;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.text-overlay :deep(.story-link):hover,
.text-overlay :deep(a):hover {
  color: #3b82f6;
}

/* Media content */
.media-content {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Loading spinner */
.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner.large {
  width: 60px;
  height: 60px;
  border-width: 4px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Media error */
.media-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: white;
}

.media-error p {
  font-size: 12px;
  opacity: 0.8;
}

/* Pause indicator */
.pause-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 12px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  color: rgba(0, 0, 0, 0.8);
}

/* UI Overlays */
.ui-overlays {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 20;
}

.ui-overlays > * {
  pointer-events: auto;
}

.bottom-ui {
  pointer-events: auto;
}

/* Gradients */
.gradient-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent);
  pointer-events: none;
}

.gradient-bottom {
  display: none; /* Hidden - bottom-ui has its own gradient */
}

/* Top UI */
.top-ui {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 16px;
  padding-top: max(16px, env(safe-area-inset-top));
}

/* Progress bars */
.progress-bars {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}

.progress-bar-wrapper {
  flex: 1;
}

.progress-bar-bg {
  height: 3px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #00c444, #00e050);
  border-radius: 2px;
  transition: width 0.05s linear;
  box-shadow: 0 0 8px rgba(0, 196, 68, 0.5);
}

/* User info row */
.user-info-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: white;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.timestamp {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.menu-button,
.close-button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.menu-button:hover,
.close-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Bottom UI */
.bottom-ui {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.7) 70%,
    transparent 100%
  );
  padding: 16px;
  padding-top: 32px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  min-height: 80px;
}

.description-section {
  margin-bottom: 12px;
}

.text-overlay-label {
  font-size: 14px;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
}

.text-overlay-label :deep(.story-link),
.text-overlay-label :deep(a) {
  color: #60a5fa;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.text-overlay-label :deep(.story-link):hover,
.text-overlay-label :deep(a):hover {
  color: #93c5fd;
}

.description-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
}

.description-text :deep(.story-link),
.description-text a {
  color: #60a5fa;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.description-text :deep(.story-link):hover,
.description-text a:hover {
  color: #93c5fd;
}

/* Input bar */
.input-bar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.input-bar.hidden {
  display: none;
}

.input-bar input {
  flex: 1;
  height: 48px;
  padding: 0 16px;
  background: var(--surface-container-high, rgba(50, 50, 50, 0.9));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
}

.input-bar input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.input-bar input:focus {
  border-color: #00c444;
  box-shadow: 0 0 0 2px rgba(0, 196, 68, 0.2);
}

.action-button {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reaction-button.reacted {
  color: #ff3b5c;
  border-color: #ff3b5c;
  background: rgba(255, 59, 92, 0.15);
}

.reaction-button.reacted:not(.loading) {
  animation: heartBeat 0.3s ease-in-out;
}

.reaction-button.loading {
  pointer-events: none;
  opacity: 0.7;
}

@keyframes heartBeat {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.button-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Popup Menu */
.popup-menu-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 80px 16px;
  z-index: 30;
}

.popup-menu {
  background: var(--surface, #1e1e1e);
  border-radius: 12px;
  overflow: hidden;
  min-width: 200px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.popup-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px;
  background: transparent;
  border: none;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.popup-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.popup-item.delete {
  color: #ef4444;
}

.popup-item .reaction-count {
  margin-left: auto;
  opacity: 0.7;
}

/* Confirm Dialog */
.confirm-dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 40;
}

.confirm-dialog {
  background: var(--surface, #1e1e1e);
  border-radius: 16px;
  padding: 24px;
  max-width: 320px;
  width: 100%;
}

.confirm-dialog h3 {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
}

.confirm-dialog p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 24px;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.dialog-actions button {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.delete-btn {
  background: #ef4444;
  border: none;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

/* Reactions Sheet */
.reactions-sheet-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  z-index: 30;
}

.reactions-sheet {
  background: var(--surface, #1e1e1e);
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
}

.sheet-handle {
  width: 40px;
  height: 4px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 2px;
  margin: 12px auto 8px;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sheet-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.total-count {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.reactions-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.loading-reactions,
.no-reactions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: rgba(255, 255, 255, 0.5);
}

.no-reactions p {
  margin-top: 16px;
  font-size: 14px;
}

.reaction-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.reaction-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.7);
}

.reaction-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reaction-user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.reaction-user-name {
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.reaction-time {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.reaction-emoji {
  font-size: 24px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

/* Delete loading overlay */
.delete-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

/* Heart Animation */
.heart-animation {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 100;
  filter: drop-shadow(0 4px 20px rgba(255, 59, 92, 0.5));
}

.heart-pop-enter-active {
  animation: heartPop 0.6s cubic-bezier(0.17, 0.89, 0.32, 1.49);
}

.heart-pop-leave-active {
  animation: heartFade 0.4s ease-out;
}

@keyframes heartPop {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@keyframes heartFade {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

/* Toast Notification */
.toast-notification {
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  color: white;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.4);
  z-index: 200;
  white-space: nowrap;
}

.toast-notification svg {
  flex-shrink: 0;
}

.toast-slide-enter-active {
  animation: toastSlideIn 0.3s ease-out;
}

.toast-slide-leave-active {
  animation: toastSlideOut 0.3s ease-in;
}

@keyframes toastSlideIn {
  0% {
    transform: translateX(-50%) translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

@keyframes toastSlideOut {
  0% {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateX(-50%) translateY(-20px);
    opacity: 0;
  }
}
</style>
